import type {
  LogisticsPartner,
  Page,
  TrackingCheckOutcome,
  TrackingRefreshOutcome,
  TrackingRepository,
  TrackingRun,
  TrackingSettingsSaved,
  TrackingSettingsState,
  TrackingSourceReport,
  UnrecognisedStatus,
  WriteResult,
} from "@estelle/domain";

import { ForbiddenError, apiFetch } from "@/adapters/api/base";

/**
 * Shipment tracking, over HTTP (#527).
 *
 * `apps/api/src/routes/tracking.ts` is the other half of this file. Every
 * refusal the server can give for an expected reason — nothing connected, just
 * checked, a value out of range, no permission — comes back here as a typed
 * outcome the screen words, and not as an exception. Only a transport failure
 * throws, with the status and path and never a body: a run's error is written
 * to carry no key, but this message reaches a screen all the same.
 *
 * Nothing here carries a partner's key and nothing writes to Shopify; the
 * routes do not have either to give.
 */

const BASE = "/api/tracking";

type Body = Record<string, unknown>;

async function bodyOf(response: Response): Promise<Body> {
  const body: unknown = await response.json().catch(() => null);
  return typeof body === "object" && body !== null && !Array.isArray(body)
    ? (body as Body)
    : {};
}

const text = (value: unknown): string | null =>
  typeof value === "string" && value !== "" ? value : null;

const seconds = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value)
    ? Math.max(1, Math.ceil(value))
    : 60;

/** The permission a 403 names, when it does. */
function permissionOf(body: Body): string | null {
  return text(body.permission);
}

/** A read: 403 is the shared no-access state, anything else not ok is a failure to show. */
async function read<T>(path: string): Promise<T> {
  const response = await apiFetch(`${BASE}${path}`);
  if (response.status === 403) {
    throw new ForbiddenError(
      `${BASE}${path}`,
      permissionOf(await bodyOf(response)) ?? undefined,
    );
  }
  if (response.status === 503) {
    throw new Error("Tracking is not set up on this server.");
  }
  if (!response.ok) {
    throw new Error(
      `Tracking request failed: ${String(response.status)} on ${path}`,
    );
  }
  return (await response.json()) as T;
}

function items<T>(body: unknown): T[] {
  const list = (body as { items?: unknown }).items;
  return Array.isArray(list) ? (list as T[]) : [];
}

export function createApiTracking(): TrackingRepository {
  return {
    listPartners: async () =>
      items<LogisticsPartner>(await read<unknown>("/partners")),

    listRuns: async (query) => {
      const search = new URLSearchParams();
      if (query?.limit !== undefined) search.set("limit", String(query.limit));
      if (query?.cursor !== undefined) search.set("cursor", query.cursor);
      if (query?.source !== undefined) search.set("source", query.source);
      const qs = search.toString();
      return read<Page<TrackingRun>>(`/runs${qs ? `?${qs}` : ""}`);
    },

    listUnrecognised: async () =>
      items<UnrecognisedStatus>(await read<unknown>("/unrecognised")),

    getSettings: () => read<TrackingSettingsState>("/settings"),

    saveSettings: async (
      patch,
    ): Promise<WriteResult<TrackingSettingsSaved>> => {
      const response = await apiFetch(`${BASE}/settings`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      });
      const body = await bodyOf(response);

      if (response.status === 403) {
        const permission = permissionOf(body) ?? "settings.company.manage";
        return {
          ok: false,
          rejection: {
            kind: "forbidden",
            message: "You do not have permission to change these settings.",
            permission,
          },
        };
      }
      // A 422 carries the rejection with a message per field, as every write does.
      if (body.ok === false && typeof body.rejection === "object") {
        return {
          ok: false,
          rejection: body.rejection as Extract<
            WriteResult<never>,
            { ok: false }
          >["rejection"],
        };
      }
      if (response.ok && body.ok === true && typeof body.data === "object") {
        return {
          ok: true,
          data: {
            settings: body.data as TrackingSettingsSaved["settings"],
            changed: body.changed === true,
          },
        };
      }
      throw new Error(
        `Tracking settings save failed: ${String(response.status)}`,
      );
    },

    checkNow: async (source): Promise<TrackingCheckOutcome> => {
      const response = await apiFetch(`${BASE}/check`, {
        method: "POST",
        ...(source === undefined
          ? {}
          : {
              headers: { "content-type": "application/json" },
              body: JSON.stringify({ source }),
            }),
      });
      const body = await bodyOf(response);

      switch (response.status) {
        case 403:
          return { kind: "forbidden", permission: permissionOf(body) };
        case 404:
        case 409:
          return {
            kind: "not_connected",
            message: text(body.error) ?? "That source is not connected.",
          };
        case 429:
          return {
            kind: "just_checked",
            retryAfterSeconds: seconds(body.retryAfterSeconds),
          };
        case 503:
          return {
            kind: "unavailable",
            message:
              text(body.error) ?? "Tracking is not set up on this server.",
          };
      }
      if (response.ok) {
        if (body.skipped === true) {
          return {
            kind: "nothing_to_check",
            reason: text(body.reason) ?? "There was nothing to check.",
          };
        }
        if (body.alreadyRunning === true) return { kind: "already_running" };
        if (body.started === true) {
          return {
            kind: "started",
            sources: Array.isArray(body.sources)
              ? body.sources.filter(
                  (entry): entry is string => typeof entry === "string",
                )
              : [],
          };
        }
      }
      throw new Error(`Tracking check failed: ${String(response.status)}`);
    },

    refreshOrder: async (orderId): Promise<TrackingRefreshOutcome> => {
      const response = await apiFetch(
        `${BASE}/orders/${encodeURIComponent(orderId)}/refresh`,
        { method: "POST" },
      );
      const body = await bodyOf(response);

      switch (response.status) {
        case 403:
          return { kind: "forbidden", permission: permissionOf(body) };
        case 400:
        case 404:
          return { kind: "not_found" };
        case 429:
          return {
            kind: "just_refreshed",
            retryAfterSeconds: seconds(body.retryAfterSeconds),
          };
        case 503:
          // "No source connected for this order" names a status; "tracking is
          // not set up on this server" does not.
          return body.status === "unavailable"
            ? { kind: "not_connected" }
            : {
                kind: "unavailable",
                message:
                  text(body.error) ?? "Tracking is not set up on this server.",
              };
      }
      if (response.ok) {
        if (body.status === "nothing_to_check") {
          return { kind: "nothing_to_check" };
        }
        if (body.status === "refreshed") {
          return {
            kind: "refreshed",
            sources: Array.isArray(body.sources)
              ? (body.sources as TrackingSourceReport[])
              : [],
          };
        }
      }
      throw new Error(`Tracking refresh failed: ${String(response.status)}`);
    },
  };
}
